<script setup lang="ts">
import { useId } from 'vue'

import type { RadioProps, RadioValue } from './types'

const model = defineModel<RadioValue | null>({ default: null })

withDefaults(defineProps<RadioProps>(), {
  disabled: false,
})

const emit = defineEmits<{ change: [value: RadioValue] }>()
const inputId = useId()

function onChange(): void {
  if (model.value !== null) emit('change', model.value)
}
</script>

<template>
  <label
    class="ui-radio inline-flex cursor-pointer items-start gap-xs font-sans text-fg"
    :class="{ 'ui-radio--disabled cursor-not-allowed opacity-60': disabled }"
    :for="inputId"
  >
    <span
      class="ui-radio__control relative grid h-[1.125rem] w-[1.125rem] flex-none place-items-center"
    >
      <input
        :id="inputId"
        v-model="model"
        class="ui-radio__input peer m-0 h-full w-full cursor-pointer appearance-none rounded-full border border-border bg-surface outline-none focus-visible:border-ring focus-visible:shadow-[0_0_0_2px_var(--ui-color-ring)] checked:border-primary forced-colors:appearance-auto"
        :class="{ 'cursor-not-allowed': disabled }"
        type="radio"
        :name="name"
        :value="value"
        :disabled="disabled"
        :aria-disabled="disabled"
        @change="onChange"
      />
      <span
        class="ui-radio__dot pointer-events-none absolute h-2.5 w-2.5 rounded-full bg-primary opacity-0 peer-checked:opacity-100 forced-colors:hidden"
        aria-hidden="true"
      />
    </span>
    <span class="ui-radio__label text-[0.8125rem] leading-[1.125rem]">{{ label }}</span>
  </label>
</template>
